import React, { useRef, useState } from "react";
import "./AgencyProfileLogo.scss";
import { Typography, IconButton, Box, CircularProgress } from "@mui/material";
import { toast } from "react-hot-toast";
import { BrandApi } from "../../../utils/api/Brand";
import { AgencyApi } from "../../../utils/api/Agency";
import { getCdnImageUrl, getImageUrl } from "../../../utils/helpers/common";
import Upload from "../../../components/icons/Upload";

const BrandProfileLogo = ({
  formik,
  brandId,
  agencyId,
  brandStatus,
  canUploadOrChange,
  onColorsGenerated,
  onUploadComplete,
}: any) => {
  const [isDragging, setIsDragging] = useState(false);
  const [isLoadingUpload, setIsLoadingUpload] = useState(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const isBrandLocked = brandStatus === "active";

  const canUploadLogo = !!canUploadOrChange;

  const getLogoDisplayUrl = (uploadRecord: any) => {
    if (!uploadRecord) return "";

    if (uploadRecord.storage_key) {
      return getCdnImageUrl(uploadRecord.storage_key);
    }

    if (uploadRecord.original_file_path) {
      return getImageUrl(uploadRecord.original_file_path);
    }

    if (typeof uploadRecord === "string") {
      return getImageUrl(uploadRecord);
    }

    return "";
  };

  const normalizeColor = (color: any) => {
    if (!color) return null;

    if (
      color?.rgb?.r !== undefined &&
      color?.rgb?.g !== undefined &&
      color?.rgb?.b !== undefined
    ) {
      return color;
    }

    if (
      color?.r !== undefined &&
      color?.g !== undefined &&
      color?.b !== undefined
    ) {
      return {
        r: color.r,
        g: color.g,
        b: color.b,
        hex:
          color.hex ||
          `#${componentToHex(color.r)}${componentToHex(color.g)}${componentToHex(color.b)}`,
      };
    }

    if (color?.hex) {
      const hex = color.hex.startsWith("#") ? color.hex : `#${color.hex}`;
      return {
        r: parseInt(hex.slice(1, 3), 16),
        g: parseInt(hex.slice(3, 5), 16),
        b: parseInt(hex.slice(5, 7), 16),
        hex: hex.toUpperCase(),
      };
    }

    return null;
  };

  function componentToHex(c: number) {
    const hex = c.toString(16);
    return hex.length === 1 ? "0" + hex : hex;
  }

  const handleImageChange = async (file: File) => {
    if (!file) {
      toast.error("Please select a valid logo file!");
      return;
    }

    const formData = new FormData();
    formData.append("brand_logo", file);
    formData.append("agency_id", agencyId);

    setIsLoadingUpload(true);

    try {
      const res = await BrandApi.uploadBrandLogo(brandId, formData);

      if (res?.data?.success) {
        const data = res.data.data;
        const upload = data?.brand_logo;
        const uploadRecord = data?.brand_logo;

        formik.setFieldValue("brandLogoUploadRecord", uploadRecord);
        // formik.setFieldValue(
        //   "brand_logo",
        //   uploadRecord?._id || uploadRecord?.original_file_path,
        // );
        const brandLogoValue =
          (typeof uploadRecord === "string" ? uploadRecord : "") ||
          uploadRecord?.original_file_path ||
          uploadRecord?.storage_key ||
          uploadRecord?._id ||
          "";

        formik.setFieldValue("brand_logo", brandLogoValue);

        formik.setFieldValue(
          "brand_base_color_primary",
          normalizeColor(data.base_color_primary),
        );
        formik.setFieldValue(
          "brand_base_color_secondary",
          normalizeColor(data.base_color_secondary),
        );
        formik.setFieldValue(
          "brand_text_color_primary",
          normalizeColor(data.text_color_primary),
        );
        formik.setFieldValue(
          "brand_text_color_secondary",
          normalizeColor(data.text_color_secondary),
        );

        if (data.theme_colors) {
          formik.setFieldValue("brand_theme_colors", data.theme_colors);
          formik.setFieldValue("theme_colors_response", data.theme_colors);
        }

        await AgencyApi.updateAgencyProfile(
          {
            logo_preference: "brand_logo",
            use_office_logo: false,
            automatic_timezone: true,
          },
          agencyId,
        );

        onColorsGenerated?.({
          base_color_primary: normalizeColor(data.base_color_primary),
          base_color_secondary: normalizeColor(data.base_color_secondary),
          text_color_primary: normalizeColor(data.text_color_primary),
          text_color_secondary: normalizeColor(data.text_color_secondary),
        });

        if (onUploadComplete) {
          try {
            await onUploadComplete();
          } catch (err) {
            console.error("onUploadComplete failed:", err);
          }
        }

        toast.success("Brand logo uploaded successfully");
      } else {
        toast.error(res?.data?.message || "Failed to upload brand logo");
      }
    } catch (error) {
      console.error("Upload error:", error);
      toast.error("An error occurred while uploading the logo");
    } finally {
      setIsLoadingUpload(false);
    }
  };

  const getDisplayUrl = () => {
    const uploadRecord = formik.values.brandLogoUploadRecord;

    if (uploadRecord) {
      return getLogoDisplayUrl(uploadRecord);
    }

    if (typeof formik.values.brand_logo === "string") {
      return getImageUrl(formik.values.brand_logo);
    }

    return "";
  };

  return (
    <div className="agency-profile-logo-container">
      {formik.values.brand_logo ? (
        <Box>
          <div className="profile-image-container">
            <img
              // src={getImageUrl(formik.values.brand_logo)}
              src={getDisplayUrl()}
              alt="Brand Logo"
            />
          </div>
          {canUploadLogo && (
            <Box mt={2} display="flex" gap={2}>
              <button
                type="button"
                className="agency-update-button"
                disabled={isLoadingUpload}
                onClick={() => fileInputRef.current?.click()}
              >
                {isLoadingUpload ? (
                  <div className="black-color-circular-progress">
                    <CircularProgress size={23} />
                  </div>
                ) : (
                  "Change logo"
                )}
              </button>

              <input
                ref={fileInputRef}
                type="file"
                hidden
                disabled={isLoadingUpload || !canUploadLogo}
                onChange={(e) =>
                  e.target.files && handleImageChange(e.target.files[0])
                }
              />
            </Box>
          )}
        </Box>
      ) : (
        <Box
          className={`profile-picture-upload ${isDragging ? "dragging" : ""}`}
          onDragOver={(e) => e.preventDefault()}
          onDragEnter={() => setIsDragging(true)}
          onDragLeave={() => setIsDragging(false)}
          onDrop={(e) => {
            e.preventDefault();
            setIsDragging(false);
            if (!canUploadLogo) {
              toast.error("Brand logo is locked by the Brand/Franchise.");
              return;
            }
            if (e.dataTransfer.files?.[0]) {
              handleImageChange(e.dataTransfer.files[0]);
            }
          }}
        >
          <Box component="label" className="upload-box">
            <input
              type="file"
              hidden
              disabled={isLoadingUpload || !canUploadLogo}
              onChange={(e) =>
                e.target.files && handleImageChange(e.target.files[0])
              }
            />

            <Box className="upload-content">
              <IconButton size="large">
                <div className="upload-icon-box">
                  {isLoadingUpload ? (
                    <div className="black-color-circular-progress">
                      <CircularProgress size={23} />
                    </div>
                  ) : (
                    <Upload />
                  )}
                </div>
              </IconButton>

              <Typography variant="body2">
                {!canUploadLogo
                  ? "Brand logo is locked by the Brand/Franchise"
                  : isDragging
                    ? "Release to upload"
                    : "Drop or Select file"}
              </Typography>
            </Box>
          </Box>
        </Box>
      )}
    </div>
  );
};

export default BrandProfileLogo;
